<template>
  <div class="yama-page">
    <!-- Game Over State -->
    <template v-if="isGameOver">
      <StageFailEnd
        :failed-answer="failedAnswer"
        @open-feature="openFeatureModal"
        @renew="refreshGame"
      />
    </template>
    
    <!-- Active Game State -->
    <template v-else>
      <!-- Game Banner -->
      <YamaBanner
        :is-start-game="isStartGame"
        :has-time-record="hasTimeRecord"
        :inject-key="$banner"
        @open-qa="openQuestionModal"
        @open-record="openRecordModal"
        @action-start="actionStart"
      />

      <!-- Main Game Content -->
      <main class="game-content" :style="mainStyle">
        <YamaGameStage
          :current-stage-name="currentStageName"
          :inject-key="$stage"
          :is-end="isEnd"
          :game-stats="gameStats"
          @choose-option="chooseAnswer"
          @finish-answer="finishAnswer"
        />
      </main>
    </template>

    <!-- Victory End State -->
    <StageVictoryEnd
      v-if="isEnd && !isGameOver"
      :is-component-modal-active="!isGameOver"
      :ranks-info="ranksInfo"
    />
  </div>
</template>

<script>
import { defineComponent } from '@vue/composition-api';
import YamaBanner from './components/YamaBanner.vue';
import YamaGameStage from './components/YamaGameStage.vue';
import StageVictoryEnd from './stageVictoryEnd/index.vue';
import StageFailEnd from './stageFailEnd/index.vue';
import { useYama } from '../../composables/useYama';

/**
 * Refactored Yama page following SOLID principles
 * Single Responsibility: Orchestrates yama game components and state
 * Open/Closed Principle: Extensible through composition and props
 * Dependency Inversion: Depends on composables and child components
 * 
 * This component maintains compatibility with the existing yama game
 * while integrating with our new SOLID architecture
 */
export default defineComponent({
  name: 'YamaPage',
  
  components: {
    YamaBanner,
    YamaGameStage,
    StageVictoryEnd,
    StageFailEnd
  },
  
  props: {
    memberLineId: {
      type: String,
      required: true
    },
    memberName: {
      type: String,
      default: ''
    },
    groupId: {
      type: [String, Number],
      required: true
    }
  },
  
  // WebSocket channels configuration
  channels: {
    AnswerRoomsChannel: {
      connected() {
        console.log("Yama WebSocket connected.");
        this.triggerLoading();
      },
      received(data) {
        try {
          const parsedData = JSON.parse(data);
          this.receivedMessage(parsedData);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      },
      disconnected() {
        console.log("Yama WebSocket disconnected");
      },
      rejected() {
        console.log("Yama WebSocket rejected");
      }
    }
  },
  
  setup(props, context) {
    // Use the yama composable for all business logic
    const yamaState = useYama(props, context);
    
    // Return all the reactive state and methods
    // This component now has a single responsibility: orchestrating the UI
    return {
      ...yamaState
    };
  }
});
</script>

<style lang="scss" scoped>
.yama-page {
  height: 100%;
  min-width: 375px;
  max-width: 480px;
  overflow: hidden;
  margin: 0 auto;
  
  .game-content {
    min-height: 563px;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    position: relative;
    display: flex;
    flex-direction: column;
  }
}

// Responsive design
@media (max-width: 768px) {
  .yama-page {
    min-width: 320px;
    max-width: 100%;
    
    .game-content {
      min-height: 500px;
    }
  }
}

// Loading states
.yama-page {
  &.loading {
    opacity: 0.8;
    pointer-events: none;
  }
}

// Game state specific styles
.yama-page {
  &.game-over {
    .game-content {
      filter: blur(2px);
    }
  }
  
  &.victory {
    .game-content {
      animation: victoryGlow 2s ease-in-out infinite alternate;
    }
  }
}

@keyframes victoryGlow {
  from {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
  }
  to {
    box-shadow: 0 0 40px rgba(255, 215, 0, 0.8);
  }
}

// Accessibility improvements
.yama-page {
  &:focus-within {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }
}
</style>